import React, { useCallback, useEffect, useState } from "react";
import {
  FaHome,
  FaEdit,
  FaTrash,
  FaSpinner,
  FaMapMarkerAlt,
  FaBuilding,
  FaCheckCircle,
  FaTimes,
  FaInfoCircle,
  FaMoneyBill,
} from "react-icons/fa";
import { MdClose } from "react-icons/md";
import { Apartment } from "../../hook/types/apartment";
import ExpenseHistoryPopup from "./ExpenseHistoryPopup";
import EditApartmentPopup from "./EditApartmentPopup";
import { useApartment } from "../../hook/useApartment";
import { useUsers } from "../../hook/useUsers";
import { User } from "../../hook/types/users";

// const [showExpenseHistory, setShowExpenseHistory] = useState(false);
// const [selectedApartmentForHistory, setSelectedApartmentForHistory] = useState<Apartment | null>(null);

const ApartmentCard = ({
  apartment,
  onClick,
  handleEdit,
  handleDelete,
   onShowExpenseHistory, 
}: {
  apartment: Apartment;
  handleEdit: () => void;
  handleDelete: () => void;
  onClick: () => void;
  onShowExpenseHistory: () => void;
}) => {
  const { getApartmentImageUrl } = useApartment();
  const [imageUrl, setImageUrl] = useState<string>("");
  const [imageLoading, setImageLoading] = useState<boolean>(true);
  const [imageError, setImageError] = useState<boolean>(false);
    const {queryClient} = useUsers()
  const uid = localStorage.getItem("R_M_uid") 
  const userData = queryClient.getQueryData(["users",uid])  as User;

useEffect(() => {
  const fetchImage = async () => {
    if (!apartment.id) {
      setImageLoading(false);
      return;
    }

    try {
      setImageLoading(true);
      setImageError(false);
      
      // Use the provided image URL if available, otherwise fetch from API
      if (apartment.apartmentImageUrl) {
       
        // Await the promise resolution
        const url = await getApartmentImageUrl(apartment.id);
        setImageUrl(url);
      }
    } catch (error) {
      console.error("Error setting image URL:", error);
      setImageError(true);
    } finally {
      setImageLoading(false);
    }
  };

  fetchImage();
}, [apartment.id, apartment.apartmentImageUrl, getApartmentImageUrl]);

  const handleImageError = () => {
    setImageError(true);
    setImageUrl("/placeholder-apartment.jpg");
  };
  
  return (
    <div className="relative hover:border-primary border-2 bg-white rounded-3xl shadow-lg overflow-hidden w-full group">
      {imageLoading ? (
          <FaSpinner className="animate-spin text-gray-400 text-2xl" />
        ) : imageError ? (
          <div className="flex flex-col items-center justify-center text-gray-400">
            <FaHome className="text-4xl mb-2" />
            <span className="text-sm">No Image</span>
          </div>
        ) : (
          <img
            src={imageUrl}
            alt={apartment.apartmentName}
            className="w-full h-64 object-cover"
            //onError={handleImageError}
            loading="lazy"
          />
        )}
      <div className="absolute flex flex-col top-0 left-0 p-4 w-72 rounded-br-3xl bg-black bg-opacity-35">
        <div className="flex items-center">
          <h2 className="text-white text-xl truncate">
            {apartment.apartmentName}
          </h2>
        </div>
        
        <div className="flex text-md items-center gap-2">
          <FaMapMarkerAlt className="text-white" />
          <span className="text-white truncate">
            {apartment.apartmentAddress}
          </span>
        </div>
        
        <div className="flex text-sm items-center gap-2">
          <FaBuilding className="text-white" />
          <span className="text-md text-white">
            {apartment.propertyHoldings?.length || 0} Properties
          </span>
        </div>
      </div>
      
      <div className="absolute bottom-0 left-0 right-0 p-6 flex justify-between items-center">
        <button
          className="bg-primary text-white font-semibold py-3 px-6 rounded-full shadow-md hover:bg-opacity-80 transition duration-300"
          onClick={onClick}
        >
          View Details
        </button>

         <button
          className="bg-blue-500 text-white rounded-full p-3 shadow-md hover:bg-blue-600 transition duration-300"
          onClick={onShowExpenseHistory}
          title="Expenses History"
        >
          <FaMoneyBill className="text-lg" />
        </button>

      </div>
      
      <div className="absolute top-4 right-4 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <button
          className="bg-white text-black rounded-full p-2 shadow-md hover:bg-gray-200 transition duration-300"
          onClick={handleEdit}
        >
          <FaEdit className="text-lg" />
        </button>
      
        <button
          className="bg-red-500 text-white rounded-full p-2 shadow-md hover:bg-red-600 transition duration-300"
          onClick={handleDelete}
        >
          <FaTrash className="text-lg" />
        </button>
      </div>
    </div>
  );
};

const ApartmentPopup = ({
  apartment,
  onClose,
}: {
  apartment: Apartment;
  onClose: () => void;
}) => {

  const [popupImageUrl, setPopupImageUrl] = useState<string>("");
const [popupImageLoading, setPopupImageLoading] = useState<boolean>(true);
  const { getApartmentImageUrl } = useApartment();
useEffect(() => {
  const fetchPopupImage = async () => {
    if (!apartment.id) {
      setPopupImageLoading(false);
      return;
    }

    try {
      setPopupImageLoading(true);
      // Use the getApartmentImageUrl function to fetch the image
      const url = await getApartmentImageUrl(apartment.id);
      setPopupImageUrl(url);
    } catch (error) {
      console.error("Error fetching popup image:", error);
      setPopupImageUrl("/placeholder-apartment.jpg");
    } finally {
      setPopupImageLoading(false);
    }
  };

  fetchPopupImage();
}, [apartment.id, getApartmentImageUrl]);

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 ">
          <h2 className="text-xl font-bold text-gray-800 flex items-center">
            <FaHome className="text-primary mr-2" /> Apartment Details
          </h2>
          <button 
            className="text-gray-500 hover:text-gray-700 transition-colors" 
            onClick={onClose}
          >
            <MdClose className="text-2xl" />
          </button>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Section 1: Image */}
            {/* Section 1: Image */}
            <div className="lg:col-span-1">
              <div className=" p-4 h-full">
             {popupImageLoading ? (
      <div className="w-full h-64 flex items-center justify-center bg-gray-100 rounded-md">
        <FaSpinner className="animate-spin text-gray-400 text-2xl" />
      </div>
    ) : (
      <img
        className="w-full h-64 object-cover rounded-md"
        src={popupImageUrl}
        alt={apartment.apartmentName}
        onError={(e) => {
          (e.target as HTMLImageElement).src = "/placeholder-apartment.jpg";
        }}
      />
    )}
              </div>
            </div>
            
            {/* Section 2: Details - UPDATED TO SINGLE LINE LAYOUT */}
<div className="lg:col-span-1">
  <div className=" p-4 h-full">
    {/* <h3 className="text-lg font-semibold mb-4 flex items-center">
      <FaInfoCircle className=" mr-2" /> Details
    </h3> */}
    <div className="space-y-3">
      {/* Apartment Name - Single Line */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-700 flex items-center min-w-[120px]">
          <FaHome className=" h-4 w-4 mr-2" /> Name:
        </span>
        <span className="text-md font-semibold text-gray-800 flex-1">
          {apartment.apartmentName}
        </span>
      </div>
      
      {/* Address - Single Line */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-700 flex items-center min-w-[120px]">
          <FaMapMarkerAlt className=" h-4 w-4 mr-2" /> Address:
        </span>
        <span className="text-gray-800 flex-1 truncate">
          {apartment.apartmentAddress || "No address"}
        </span>
      </div>
      
      {/* Status - Single Line */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-700 flex items-center min-w-[120px]">
          <FaInfoCircle className=" h-4 w-4 mr-2" /> Status:
        </span>
        <span className={`font-bold ${apartment.isActive ? "text-green-500" : "text-red-500"}`}>
          {apartment.isActive ? "Active" : "Inactive"}
        </span>
      </div>
      
      {/* Total Properties - Single Line */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-700 min-w-[120px]">
          Total Properties:
        </span>
        <span className="text-gray-800 font-semibold">
          {apartment.propertyHoldings?.length || 0}
        </span>
      </div>
    </div>
  </div>
</div>
            
            {/* Section 3: Holding Properties */}
            <div className="lg:col-span-1">
              <div className=" p-4 h-full">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <FaBuilding className="text-primary mr-2" /> Holding Properties
                </h3>
                
                {/* {apartment.propertyHoldings && apartment.propertyHoldings.length > 0 ? (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {apartment.propertyHoldings.map((property, index) => (
                      <div key={index} className="bg-white p-3 rounded-lg border border-gray-200">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                           <p className="text-sm text-gray-600 mt-1">
                              Name:
                              {property.propertyName}
                            </p>
                          </div>
                          <span className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">
                            #{index + 1}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FaBuilding className="mx-auto h-12 w-12 text-gray-300 mb-2" />
                    <p>No properties associated with this apartment</p>
                  </div>
                )} */}
              
{apartment.propertyHoldings && apartment.propertyHoldings.length > 0 ? (
  <div className="space-y-3 max-h-64 overflow-y-auto">
    {apartment.propertyHoldings.map((property, index) => {
      // Check if property is a number (ID) or Property object
      const propertyName = typeof property === 'object' 
        ? property.propertyName 
        : `Property ID: ${property}`;
      
      return (
        <div key={index} className="bg-white p-3 rounded-lg border border-gray-200">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <p className="text-sm text-gray-600 mt-1">
                Name: {propertyName}
              </p>
            </div>
            <span className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">
              #{index + 1}
            </span>
          </div>
        </div>
      );
    })}
  </div>
) : (
  <div className="text-center py-8 text-gray-500">
    <FaBuilding className="mx-auto h-12 w-12 text-gray-300 mb-2" />
    <p>No properties associated with this apartment</p>
  </div>
)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


interface ApartmentListProps {
  apartments: Apartment[];
  onEdit: (apartment: Apartment, id: string | number) => void;
  onDelete: (id: string | number) => void;
}

const ApartmentList: React.FC<ApartmentListProps> = ({
  apartments,
  onEdit,
  onDelete
}) => {
  const [selectedApartment, setSelectedApartment] = useState<Apartment | null>(null);
    const [showExpenseHistory, setShowExpenseHistory] = useState(false);
  const [selectedApartmentForHistory, setSelectedApartmentForHistory] = useState<Apartment | null>(null);
const [editingApartment, setEditingApartment] = useState<Apartment | null>(null); // Add this state
  const [isLoading, setIsLoading] = useState(false); // Add loading state
 // Add these handler functions
// In ApartmentList component
const handleEditSubmit = async (formData: Apartment) => {
  setIsLoading(true);
  try {
    // This should call the parent's onEdit function which handles the API call
    await onEdit(formData, formData.id || '');
    setEditingApartment(null);
    setSelectedApartment(null);
  } catch (error) {
    console.error("Error editing apartment:", error);
  } finally {
    setIsLoading(false);
  }
};

  const handleEditCancel = () => {
    setEditingApartment(null);
  };
  if (apartments.length === 0) {
    return (
      <div className="h-full w-full text-foreground justify-center flex items-center">
        <p>No Data</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
      {apartments.map((apartment) => (
 
<ApartmentCard
  key={apartment.id}
  apartment={apartment}
  onClick={() => setSelectedApartment(apartment)}
  // handleEdit={() => onEdit(apartment, apartment.id || '')} 
    //handleEdit={() => setEditingApartment(apartment)}
    handleEdit={() => onEdit(apartment, apartment.id || '')}
  handleDelete={() => onDelete(apartment.id || '')} 
   onShowExpenseHistory={() => {
            setSelectedApartmentForHistory(apartment);
            setShowExpenseHistory(true);
          }}
/>
      ))}

      {selectedApartment && (
        <ApartmentPopup
          apartment={selectedApartment}
          onClose={() => setSelectedApartment(null)}
        />
      )}

       {showExpenseHistory && selectedApartmentForHistory && (
        <ExpenseHistoryPopup
          isOpen={showExpenseHistory}
          onClose={() => {
            setShowExpenseHistory(false);
            setSelectedApartmentForHistory(null);
          }}
          apartmentId={selectedApartmentForHistory.id || ''}
          apartmentName={selectedApartmentForHistory.apartmentName}
        />
      )}
       {/* {editingApartment && (
        <EditApartmentPopup
          apartment={editingApartment}
          onClose={() => setEditingApartment(null)}
          onSubmit={handleEditSubmit}
          onCancel={handleEditCancel}
          isOpen={!!editingApartment}
          id={editingApartment.id || ''}
          isLoading={isLoading}
        />
      )} */}
    </div>
  );
};

export default ApartmentList;